import { FONT_FAMILY } from "@/constants";
import Box from "@mui/material/Box";
import { ReactNode } from "react";
import {
  AUTH_SHORT,
  AUTH_SHORTEST,
  AUTH_WIDE_TALL,
  authSectionGapSx,
} from "./authSpacing";

interface AuthSplitLayoutProps {
  /** Brand panel (renders its own <aside>). Stacks above the form below md. */
  panel: ReactNode;
  /** Right-aligned row at the top of the form pane (e.g. "Already have an account?"). */
  header?: ReactNode;
  children: ReactNode;
}

// Header row (20px line + margin). Mirrored as a bottom spacer so the form
// centres on the pane rather than on the space under the header.
const HEADER_BLOCK = "36px";

export default function AuthSplitLayout({
  panel,
  header,
  children,
}: AuthSplitLayoutProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "minmax(0, 45fr) minmax(0, 55fr)",
          // Ultra-wide: stop the panel growing past ~960px; the form pane takes the rest.
          xl: "minmax(0, min(45%, 960px)) minmax(0, 1fr)",
        },
        // max-content: the panel (overflow:hidden) would otherwise shrink when the form overflows.
        // md: never shorter than either column, so a short viewport scrolls the page instead.
        gridTemplateRows: {
          xs: "max-content 1fr",
          md: "minmax(max-content, 1fr)",
        },
        // html/body are overflow:hidden app-wide (src/index.css), so this page owns its scroll.
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        overflowY: "auto",
      }}
    >
      {panel}
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: { xs: "32px 16px 48px", sm: "16px 24px", md: 3, lg: "24px 32px" },
          [AUTH_SHORT]: { py: 2 },
          [AUTH_SHORTEST]: { py: 1.5 },
          // Bottom twin of the header row, so `my: auto` below centres optically.
          ...(header && {
            "&::after": {
              content: '""',
              flexShrink: 0,
              height: { xs: 0, md: HEADER_BLOCK },
              [AUTH_SHORT]: { height: 0 },
            },
          }),
          // MUI falls back to Roboto for these slots; keep auth text on the brand font.
          "& .MuiFormHelperText-root, & .MuiAlert-message, & .MuiButton-root": {
            fontFamily: FONT_FAMILY,
          },
        }}
      >
        {header && (
          <Box
            sx={{
              alignSelf: "stretch",
              display: "flex",
              justifyContent: "flex-end",
              textAlign: "right",
              mb: 2,
              [AUTH_SHORT]: { mb: 1 },
              [AUTH_SHORTEST]: { mb: 0.5 },
            }}
          >
            {header}
          </Box>
        )}
        <Box
          sx={{
            width: "100%",
            // Safe centring: auto margins collapse to 0 when the column is taller
            // than the pane, so it top-aligns and scrolls instead of clipping.
            my: "auto",
            // 400 = Google's GIS button max width, so it lines up with the inputs.
            maxWidth: 400,
            // 24"+ monitors: a slightly larger page heading to match the panel's.
            [AUTH_WIDE_TALL]: { "& h1": { fontSize: "28px", lineHeight: 1.3 } },
            display: "flex",
            flexDirection: "column",
            ...authSectionGapSx,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
